import {
  ChartV2Horizon,
  ChartV2Interval,
  ChartV2LinePoint,
  ChartV2OhlcPoint,
  ChartV2Series,
} from '../types/chart-v2.types';

const DAY_MS = 24 * 60 * 60 * 1000;
const MOCK_HISTORY_DAYS = 365 * 15;

interface GenerateLineSeriesOptions {
  startPrice: number;
  days: number;
  volatility: number;
  seed?: number;
}

const generateLineSeries = ({
  startPrice,
  days,
  volatility,
  seed = 1,
}: GenerateLineSeriesOptions): ChartV2LinePoint[] => {
  const points: ChartV2LinePoint[] = [];
  let price = startPrice;
  const now = Date.UTC(2026, 3, 17);
  let rand = seed;

  for (let i = days - 1; i >= 0; i -= 1) {
    rand = (rand * 9301 + 49297) % 233280;
    const noise = rand / 233280 - 0.5;
    price = Math.max(1, price + noise * volatility);
    points.push({
      time: now - i * DAY_MS,
      value: Number(price.toFixed(2)),
    });
  }

  return points;
};

interface GenerateOhlcSeriesOptions extends GenerateLineSeriesOptions {
  intrabarVolatility?: number;
}

const generateOhlcSeries = ({
  startPrice,
  days,
  volatility,
  intrabarVolatility = 2,
  seed = 1,
}: GenerateOhlcSeriesOptions): ChartV2OhlcPoint[] => {
  const points: ChartV2OhlcPoint[] = [];
  let price = startPrice;
  const now = Date.UTC(2026, 3, 17);
  let rand = seed;

  const random = () => {
    rand = (rand * 9301 + 49297) % 233280;
    return rand / 233280;
  };

  for (let i = days - 1; i >= 0; i -= 1) {
    const open = price;
    const close = Math.max(1, open + (random() - 0.5) * volatility);
    const spread = Math.abs(close - open) + random() * intrabarVolatility;
    const high = Math.max(open, close) + spread * 0.5;
    const low = Math.min(open, close) - spread * 0.5;

    points.push({
      time: now - i * DAY_MS,
      open: Number(open.toFixed(2)),
      close: Number(close.toFixed(2)),
      high: Number(high.toFixed(2)),
      low: Number(Math.max(1, low).toFixed(2)),
    });
    price = close;
  }

  return points;
};

export const mockPrimarySeries: ChartV2Series = {
  id: 'primary',
  label: 'AAPL',
  seriesType: 'line',
  data: generateOhlcSeries({
    startPrice: 180,
    days: MOCK_HISTORY_DAYS,
    volatility: 4,
    seed: 1,
  }),
};

export const mockSecondarySeries: ChartV2Series = {
  id: 'secondary',
  label: 'MSFT',
  seriesType: 'line',
  data: generateLineSeries({
    startPrice: 410,
    days: MOCK_HISTORY_DAYS,
    volatility: 6,
    seed: 7,
  }),
};

export const mockTertiarySeries: ChartV2Series = {
  id: 'tertiary',
  label: 'NVDA',
  seriesType: 'line',
  data: generateLineSeries({
    startPrice: 20,
    days: MOCK_HISTORY_DAYS,
    volatility: 1.5,
    seed: 23,
  }),
};

export const mockChartV2Series: ChartV2Series[] = [
  mockPrimarySeries,
  mockSecondarySeries,
  mockTertiarySeries,
];

export const mockHorizons: ChartV2Horizon[] = [
  '1D',
  '1W',
  '2W',
  '1M',
  '2M',
  '3M',
  '6M',
  '1Y',
  '3Y',
  '5Y',
  '10Y',
  'MAX',
];

export const mockIntervals: ChartV2Interval[] = [
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '1D',
  '1W',
  '1M',
];

export const mockDefaultHorizon: ChartV2Horizon = 'MAX';
export const mockDefaultInterval: ChartV2Interval = '1D';

export const mockChartV2Props = {
  series: mockChartV2Series,
  horizon: mockDefaultHorizon,
  interval: mockDefaultInterval,
  availableHorizons: mockHorizons,
  availableIntervals: mockIntervals,
};
